import { Combobox, type ComboboxItem, Label } from '@consigli/facade';
import { useDebouncedSearch, useSearchOracleProjectsQuery } from '@consigli/hooks';
import { type FC, useState, useCallback, useMemo } from 'react';
import { Control, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { toOracleComboboxItems } from '@/util/oracle-projects';
import { ProjectInputs } from '@/util/types';

interface OracleProjectFieldProps {
  control: Control<ProjectInputs>;
  onProjectChange?: (
    projectNumber: string | null,
    projectName: string | null,
    customerName: string | null,
    projectTypeClassCode: string | null,
  ) => void;
}

export const OracleProjectField: FC<OracleProjectFieldProps> = ({ control, onProjectChange }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<ComboboxItem | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const debouncedQuery = useDebouncedSearch(searchQuery, 500);

  const { currentData, isLoading, isFetching, isError, error } = useSearchOracleProjectsQuery(
    { search: debouncedQuery },
    { skip: !debouncedQuery || accessDenied },
  );

  const is403 = isError && (error as { status?: number })?.status === 403;

  if (is403 && !accessDenied) {
    setAccessDenied(true);
  }

  const { field, fieldState } = useController({
    name: 'oracleProjectNumber',
    control,
    rules: { required: `${t('project-creation.oracle-project-required')}*` },
  });

  const results = useMemo(() => currentData?.results ?? [], [currentData]);
  const isDebouncing = Boolean(searchQuery) && searchQuery !== debouncedQuery;
  const isPending =
    Boolean(debouncedQuery) && currentData === undefined && !isError && !accessDenied;
  const isBusy = isLoading || isFetching || isDebouncing || isPending;

  // Shared with the Settings tab's picker, which offers the same rows.
  const items: ComboboxItem[] = toOracleComboboxItems(results, t);

  const handleSelect = useCallback(
    (item: ComboboxItem) => {
      setSelectedItem(item);
      setSearchQuery('');
      field.onChange(item.key);
      const project = results.find((p) => p.projectNumber === item.key);
      onProjectChange?.(
        item.key,
        item.label,
        project?.customerName ?? null,
        project?.projectTypeClassCode ?? null,
      );
    },
    [field, onProjectChange, results],
  );

  const handleClear = useCallback(() => {
    setSelectedItem(null);
    setSearchQuery('');
    field.onChange(undefined);
    onProjectChange?.(null, null, null, null);
  }, [field, onProjectChange]);

  return (
    <div>
      <Label text={t('project-creation.oracle-project-label')} />
      <p className="text-xs text-day-neutral-subtle -mt-1.5 mb-1">
        {t('project-creation.oracle-project-hint')}
      </p>

      <Combobox
        items={items}
        selectedItem={selectedItem}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSelect={handleSelect}
        onClear={handleClear}
        isLoading={isBusy}
        isError={isError}
        errorMessage={t(
          is403
            ? 'project-creation.oracle-not-authorized'
            : 'project-creation.oracle-project-search-error',
        )}
        placeholder={t('project-creation.oracle-project-placeholder')}
        noResultsMessage={t('project-creation.oracle-project-no-results')}
        loadingMessage={t('project-creation.oracle-project-searching')}
      />

      {fieldState.invalid && (
        <div className="text-sm my-1 text-error-dark">{fieldState.error?.message}</div>
      )}
    </div>
  );
};
